/**
 * /admin/devices — admin CRUD over the devices aggregate (§9.3).
 *
 * Mirrors `AdminMetersPage`:
 *   - filters: site_slug, kind — both optional, forwarded as query
 *     params unchanged. (No `device_slug` / `status` filters server-side.)
 *   - per-row actions: Edit (PATCH) and Delete (DELETE → 409 → Retire)
 *   - Create button at the page top opens the same form modal in
 *     create mode.
 *
 * Differences from the meters table:
 *   - displays the `meter_count` column so operators can see at a
 *     glance which devices still own channels (and therefore can't
 *     be hard-deleted),
 *   - the kind cell is decorated with the same icon/color the device
 *     tree uses (`getDeviceKindMeta`) so visual identity stays
 *     consistent across pages.
 */
import {
  ActionIcon,
  Badge,
  Breadcrumbs,
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconDeviceDesktop,
  IconEdit,
  IconPlus,
  IconRefresh,
  IconTrash,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAdminDevices } from "@/api/queries";
import { DEVICE_KIND_SUGGESTIONS, type AdminDevice } from "@/api/types";
import { DeviceDeleteConfirm } from "@/components/DeviceDeleteConfirm";
import { DeviceFormModal } from "@/components/DeviceFormModal";
import { ErrorBanner } from "@/components/ErrorBanner";
import { SiteSelect } from "@/components/SiteSelect";
import { getDeviceKindMeta } from "@/components/deviceKinds";

const PAGE_SIZE = 50;

type FiltersState = {
  site_slug: string;
  kind: string;
};

const EMPTY_FILTERS: FiltersState = {
  site_slug: "",
  kind: "",
};

type ModalState =
  | { kind: "none" }
  | { kind: "create" }
  | { kind: "edit"; device: AdminDevice }
  | { kind: "delete"; device: AdminDevice };

export function AdminDevicesPage() {
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<ModalState>({ kind: "none" });

  const queryParams = useMemo(
    () => ({
      site_slug: filters.site_slug.trim() || undefined,
      kind: filters.kind || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [filters, page],
  );

  const devicesQuery = useAdminDevices(queryParams);

  const items = devicesQuery.data?.items ?? [];
  const total = devicesQuery.data?.page.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setFilter = (k: keyof FiltersState, v: string) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  return (
    <Stack>
      <Breadcrumbs>
        <Text>Admin</Text>
        <Text>Devices</Text>
      </Breadcrumbs>

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={1}>Devices</Title>
          <Text c="dimmed" size="sm">
            Tenant-scoped devices across every site. Edit display name / kind
            / status here; re-home a meter to a different device through the
            meter endpoint (see DEVELOPER_CONTINUATION_GUIDE §9.3).
          </Text>
        </Stack>
        <Group>
          <Tooltip label="Refresh list">
            <ActionIcon
              variant="default"
              size="lg"
              onClick={() => devicesQuery.refetch()}
              loading={devicesQuery.isFetching}
              aria-label="Refresh"
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setModal({ kind: "create" })}
          >
            New device
          </Button>
        </Group>
      </Group>

      <Paper withBorder p="md" radius="md">
        <Group gap="sm" wrap="wrap">
          <SiteSelect
            label="Site"
            placeholder="any"
            clearable
            value={filters.site_slug || null}
            onChange={(v) => setFilter("site_slug", v ?? "")}
            w={240}
          />
          <Select
            label="Kind"
            placeholder="any"
            data={[...DEVICE_KIND_SUGGESTIONS]}
            clearable
            value={filters.kind || null}
            onChange={(v) => setFilter("kind", v ?? "")}
            w={200}
          />
        </Group>
      </Paper>

      {devicesQuery.error && <ErrorBanner error={devicesQuery.error} />}

      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconDeviceDesktop size={20} />
            <Title order={3}>Results</Title>
          </Group>
          <Text c="dimmed" size="sm">
            {total} device{total === 1 ? "" : "s"}
          </Text>
        </Group>

        {devicesQuery.isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : items.length === 0 ? (
          <Text c="dimmed">No devices match the current filters.</Text>
        ) : (
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Site</Table.Th>
                <Table.Th>Kind</Table.Th>
                <Table.Th>Meters</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((device) => {
                const meta = getDeviceKindMeta(device.kind);
                const KindIcon = meta.Icon;
                return (
                  <Table.Tr key={device.id}>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text fw={500}>{device.display_name}</Text>
                        <Text c="dimmed" size="xs">
                          <Link
                            to={`/sites/${device.site_slug}/devices/${device.slug}`}
                          >
                            {device.slug}
                          </Link>
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        <Link to={`/sites/${device.site_slug}`}>{device.site_slug}</Link>
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <ThemeIcon variant="light" color={meta.color} size="sm" radius="sm">
                          <KindIcon size={14} />
                        </ThemeIcon>
                        <Badge variant="default" tt="lowercase">
                          {device.kind}
                        </Badge>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {device.meter_count} meter{device.meter_count === 1 ? "" : "s"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={device.status === "active" ? "green" : "gray"}
                      >
                        {device.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        <Tooltip label="Edit">
                          <ActionIcon
                            variant="subtle"
                            aria-label={`Edit ${device.display_name}`}
                            onClick={() => setModal({ kind: "edit", device })}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete (or retire)">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={`Delete ${device.display_name}`}
                            onClick={() => setModal({ kind: "delete", device })}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        {totalPages > 1 && (
          <Group justify="center" mt="md">
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Paper>

      <DeviceFormModal
        opened={modal.kind === "create"}
        onClose={() => setModal({ kind: "none" })}
        mode="create"
      />
      <DeviceFormModal
        opened={modal.kind === "edit"}
        onClose={() => setModal({ kind: "none" })}
        mode="edit"
        device={modal.kind === "edit" ? modal.device : undefined}
      />
      <DeviceDeleteConfirm
        opened={modal.kind === "delete"}
        onClose={() => setModal({ kind: "none" })}
        device={modal.kind === "delete" ? modal.device : null}
      />
    </Stack>
  );
}
